import axios from "axios";
import React from "react";

/**
 * Asynchronously checks if an email exists for a given client ID.
 *
 * This function sends a GET request to a specific API endpoint to verify
 * if the provided email is associated with the specified client ID.
 *
 * @param {string} clientId - The unique identifier of the client.
 * @param {string} email - The email address to check for existence.
 * @returns {Promise<boolean>} A promise that resolves to `true` if the email exists, otherwise `false`.
 */
export const checkEmailExists = async (clientId: string, email: string): Promise<boolean> => {
    const response = await axios.get(
        `api/customers/${clientId}/contacts/check-email?email=${email}`
    );

    return response.data.exists;
};

/**
 * Validates the uniqueness of an email address for a specific client.
 *
 * Asynchronously checks whether the provided email address already exists
 * for the given client and returns a corresponding validation message.
 *
 * @param {string} clientId - The unique identifier for the client.
 * @param {string} email - The email address to be validated.
 * @returns {Promise<string>} A promise that resolves to a string:
 * - Returns a message indicating the email address already exists if it is in use.
 * - Returns an empty string if the email address is unique.
 * - Returns an error message if the validation process encounters an issue.
 */
export const handleEmailValidation = async (clientId: string, email: string) => {
    let response = '';
    try {
        const exists = await checkEmailExists(clientId, email);
        response = exists
            ? 'Cette adresse e-mail existe déjà.'
            : '';
    } catch (error) {
        response = "Une erreur s'est produite lors de la validation de l'adresse e-mail.";
    }

    return response;
};

/**
 * Updates the email state and validates the email input asynchronously.
 *
 * @param {React.ChangeEvent<HTMLInputElement>} event - The event triggered by an input field change.
 * @param {React.Dispatch<React.SetStateAction<string>>} setEmail - The state setter function for updating the email value.
 * @param {string} clientId - A unique identifier for the client, used for email validation.
 * @param {React.Dispatch<React.SetStateAction<string>>} setEmailError - The state setter function for updating the email error message.
 */
export const handleEmailChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setEmail: React.Dispatch<React.SetStateAction<string>>,
    clientId: string,
    setEmailError: React.Dispatch<React.SetStateAction<string>>
) => {
    const newEmail: string = event.target.value;
    setEmail(newEmail);
    const error = await handleEmailValidation(clientId, newEmail);
    setEmailError(error);
};